import type { Editor } from "@tiptap/core"

type Type = "row" | "column"

export type TableHandleEventDetail = {
  type: Type
  referenceElement: HTMLElement
}

export type TableAddAtLimitEventDetail = {
  type: Type
}

export type TableEventType =
  | { name: "handleClick"; detail: TableHandleEventDetail }
  | { name: "addAtLimit"; detail: TableAddAtLimitEventDetail }

type EventName = TableEventType["name"]
type EventDetail<TName extends EventName> = Extract<
  TableEventType,
  { name: TName }
>["detail"]

export class TableEvent extends CustomEvent<TableEventType["detail"]> {
  constructor({ name, detail }: TableEventType) {
    super(name, { detail })
  }

  static dispatch<TName extends EventName>(
    name: TName,
    args: { editor: Editor } & EventDetail<TName>,
  ) {
    const { editor, ...detail } = args
    editor.view.dom.dispatchEvent(
      new TableEvent({ name, detail } as TableEventType),
    )
  }

  static listen<TName extends EventName>(
    name: TName,
    args: {
      editor: Editor
      callback: (detail: EventDetail<TName>) => void
    },
  ) {
    const { editor, callback } = args
    const abortController = new AbortController()
    editor.view.dom.addEventListener(
      name,
      (event) => {
        const { detail } = event as CustomEvent<EventDetail<TName>>
        if (detail) callback(detail)
      },
      { signal: abortController.signal },
    )

    return () => abortController.abort()
  }
}
